/** The areas of a customer project. Chat, data, skills, automations and integrations exist so far; the others are placeholders. */
export type Section = 'chat' | 'data' | 'skills' | 'automations' | 'approvals' | 'integrations' | 'chats';

export const SECTIONS: readonly Section[] = ['chat', 'data', 'skills', 'automations', 'approvals', 'integrations', 'chats'];

/** Address segment after /<customer>; the chat lives at the customer's address itself. */
const SLUG: Record<Section, string> = {
  chat: '',
  data: 'data',
  skills: 'skills',
  automations: 'automations',
  approvals: 'approvals',
  integrations: 'integrations',
  chats: 'chats',
};

/** Unknown segments open the chat. */
export function sectionFromPath(pathname: string): Section {
  const slug = pathname.split('/')[2] ?? '';
  return SECTIONS.find((section) => SLUG[section] === slug) ?? 'chat';
}

export function projectUrl(projectId: string, section: Section = 'chat'): string {
  const slug = SLUG[section];
  return `/${encodeURIComponent(projectId)}${slug === '' ? '' : `/${slug}`}`;
}

/** Pages below /<customer>/automations; each run log has its own address, e.g. for links from notifications. */
export type AutomationRoute =
  | { view: 'list' | 'schedule' | 'runs' }
  | { view: 'automation'; id: string }
  | { view: 'run'; id: string; runId: string };

function decode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** `schedule` and `runs` are reserved; automation ids are UUIDs. Unknown deeper paths open the automation. */
export function automationRouteFromPath(pathname: string): AutomationRoute {
  const [first, second, third] = pathname.split('/').slice(3).filter((segment) => segment !== '').map(decode);
  if (first === undefined) return { view: 'list' };
  if (second === undefined && (first === 'schedule' || first === 'runs')) return { view: first };
  if (second === 'runs' && third !== undefined) return { view: 'run', id: first, runId: third };
  return { view: 'automation', id: first };
}

export function automationUrl(projectId: string, route: AutomationRoute): string {
  const base = projectUrl(projectId, 'automations');
  switch (route.view) {
    case 'list':
      return base;
    case 'schedule':
    case 'runs':
      return `${base}/${route.view}`;
    case 'automation':
      return `${base}/${encodeURIComponent(route.id)}`;
    case 'run':
      return `${base}/${encodeURIComponent(route.id)}/runs/${encodeURIComponent(route.runId)}`;
  }
}
